import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Grid2,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import BankVerbindungImage from "@images/add/Bankverbindung.svg?react";
import ManuellImage from "@images/add/Manuell.svg?react";
import { useBankImportReturnUrl } from "@hooks/useBankImportReturnUrl";
import { useBankSelectModal } from "@components/BankSelect";
import { ImportType } from "../types";

const StyledBox = styled(Box)(({ theme }) => ({
  borderRadius: "12px",
  border: `1px solid ${theme.palette.border.secondary}`,
  padding: theme.spacing(4, 5),

  [theme.breakpoints.down("sm")]: {
    width: "100%",

    "& svg": {
      width: "100%",
      height: "auto",
    },
  },
}));

const StyledGrid2 = styled(Grid2)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  "& .bank-background": {
    fill: theme.palette.background.info,
  },

  "& .bank-icon-background": {
    fill: theme.palette.primary.dark,
  },

  "& .bank-lock": {
    fill: theme.palette.primary.main,
  },

  "& .bank-link": {
    fill: theme.palette.primary.light,
  },

  "& .bank-link-manual": {
    stroke: theme.palette.primary.light,
  },

  "& .bank-success": {
    fill: theme.palette.success.main,
  },

  "& .bank-list-header": {
    fill: theme.palette.primary.main,
  },

  "& .paper-background": {
    fill: theme.palette.primary.lighter,
  },

  "& .paper-corner": {
    fill: theme.palette.primary.light,
  },

  "& .pdf-background": {
    fill: theme.palette.warning.main,
  },
}));

interface AccountAddSelectionProps {
  onSelection?: (importType: ImportType) => void;
  importType: ImportType;
}

export const AccountAddSelection = ({
  onSelection,
  importType,
}: AccountAddSelectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { saveReturnUrl } = useBankImportReturnUrl();
  const { openBankSelect, bankSelectModal } = useBankSelectModal();

  return (
    <Grid2 container spacing={2} columnSpacing={4}>
      <StyledGrid2 size={{ xs: 12, sm: 12, md: 6 }}>
        <StyledBox>
          <Box mb={4}>
            <BankVerbindungImage />
          </Box>
          <Button
            variant="contained"
            color="primary"
            data-testid="import-bank"
            fullWidth
            onClick={() => {
              saveReturnUrl();
              openBankSelect();
            }}
          >
            <Typography
              color={theme.palette.button.primaryButton.textOnDefault}
            >
              {t("accounts.add.importBank")}
            </Typography>
          </Button>
        </StyledBox>
      </StyledGrid2>
      <StyledGrid2 size={{ xs: 12, sm: 12, md: 6 }}>
        <StyledBox>
          <Box mb={4}>
            <ManuellImage />
          </Box>
          <Button
            variant="contained"
            color="primary"
            data-testid="add-manually"
            onClick={() => onSelection?.({ ...importType, type: "manually" })}
            fullWidth
          >
            <Typography
              color={theme.palette.button.primaryButton.textOnDefault}
            >
              {t("accounts.add.importManual")}
            </Typography>
          </Button>
        </StyledBox>
      </StyledGrid2>
      {bankSelectModal}
    </Grid2>
  );
};
